/**
 * LaunchDarkly for the login routes.
 *
 * `Login` is registered *outside* the `Shell` (see `container/src/main.tsx`), so
 * the `FeatureFlagWrapper` that serves the rest of the app is not above us —
 * there are no flags on this page unless we mount our own provider. There is
 * also no user yet, so the context is anonymous: flags read here can only be
 * targeted on environment-level attributes, never on the person signing in.
 *
 * Unlike `Shell/FeatureFlags.tsx`, this deliberately does **not** suspend.
 * That wrapper can afford to wait because it sits behind the `AuthGuard`; this
 * one is the sign-in page itself, and holding it on a third-party service would
 * make LaunchDarkly an availability dependency of logging in. `LDProvider`
 * initializes on mount and streams values in afterwards, which means every flag
 * read here starts as `undefined` and must fail towards the safe default.
 */

import { LDProvider } from "launchdarkly-react-client-sdk";
import type { FC, PropsWithChildren } from "react";
import { LAUNCH_DARKLY_KEYS } from "@granular/fabric3-definitions";
import { getEnvironment } from "../../helpers/environment";

/** Flags read by the login routes. */
export type LoginFeatureFlags = {
  /**
   * Whether to hide the "Sign in as Corteva Team Member" button.
   *
   * `undefined` until LaunchDarkly resolves, and on any page load where it
   * never does. Corteva is still a live employee sign-in path, so consumers
   * must keep the button unless this is explicitly `true` — hiding it on
   * `undefined` would lock employees out whenever LD is slow or unreachable.
   * See `SelfIdFlow`.
   */
  "app-login-hide-corteva-button": boolean | undefined;
};

export const LoginFeatureFlagsProvider: FC<PropsWithChildren> = ({
  children,
}) => (
  <LDProvider
    clientSideID={LAUNCH_DARKLY_KEYS[getEnvironment()]}
    // Match the rest of the workspace: flag keys stay kebab-case as authored.
    reactOptions={{ useCamelCaseFlagKeys: false }}
    // Returning visitors get their cached values on the first render instead of
    // waiting on the network.
    options={{ bootstrap: "localStorage" }}
  >
    {children}
  </LDProvider>
);
